import { computed, inject, Injectable, signal, untracked } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { EMPTY, Observable } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';
import { Geolocation, PermissionStatus } from '@capacitor/geolocation';
import { DynamicFlowService } from '../services/dynamic-flow.service';
import { DynamicFlowContext, DynamicFlowFormField, DynamicFlowFormValue, DynamicFlowLocationAnswer, DynamicFlowOption, DynamicFlowRawFormValues, DynamicFlowStep, DynamicFlowStepType, DYNAMIC_FLOW_INITIAL_STEP } from '../model/dynamic-flow.types';
import { DynamicFormService } from '@app/shared/components/dynamic-form/services/dynamic-form.service';
import { DynamicFormControl } from '@app/shared/components/dynamic-form/model/dynamic-form-control.model';
import { GoogleMapsService, PlaceDetails } from '@app/core/services/google-maps.service';
import { AnalyticsService } from '@app/core/services/analytics.service';
import { AnalyticsEvents } from '@app/core/enums/analytics-events.enum';
import { DeepLinkService } from '@app/core/services/deep-link.service';
import { ToastSandbox } from '@app/core/sandboxes/toast.sandbox';

@Injectable({ providedIn: 'root' })
export class DynamicFlowSandbox {
  private readonly dynamicFlowService = inject(DynamicFlowService);
  private readonly dynamicFormService = inject(DynamicFormService);
  private readonly googleMapsService = inject(GoogleMapsService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly deeplinkService = inject(DeepLinkService);
  private readonly toastSandbox = inject(ToastSandbox);

  currentStep = signal<DynamicFlowStep>(null);
  showValidationMessage = signal(false);
  loadingFlow = signal(false);
  loadingNext = signal(false);
  loadingPrevious = signal(false);

  private readonly context = signal<DynamicFlowContext | null>(null);

  private formFields = computed<DynamicFlowFormField[] | null>(() => {
    const detail = this.currentStep()?.step;
    return detail?.type === DynamicFlowStepType.FORM ? detail.form.fields : null;
  });

  formControls = computed<DynamicFormControl[]>(() => {
    const fields = this.formFields();
    return fields ? this.toFormControls(fields) : [];
  });

  dynamicForm = computed<FormGroup | null>(() => {
    const controls = this.formControls();
    return controls.length ? this.dynamicFormService.toFormGroup(controls) : null;
  });

  startFlow(flowId: string, externalId?: string): void {
    this.context.set(externalId ? { externalId } : null);
    setTimeout(() => {
      this.loadingFlow.set(true);
    }, 200);
    this.dynamicFlowService
      .postStep(flowId, this.withContext(DYNAMIC_FLOW_INITIAL_STEP), 'forward')
      .pipe(
        tap((dynamicFlowStep) => this.currentStep.set(dynamicFlowStep)),
        tap((dynamicFlowStep) => this.logDynamicFlowStep(dynamicFlowStep, flowId)),
        finalize(() => this.loadingFlow.set(false))
      ).subscribe();
  }

  next(flowId: string): Observable<DynamicFlowStep> {
    if (this.isCurrentStepValid()) {
      this.showValidationMessage.set(false);
      this.loadingNext.set(true);
      return this.dynamicFlowService
        .postStep(flowId, this.withContext(this.currentStep()), 'forward')
        .pipe(
          tap((dynamicFlowStep) => this.currentStep.set(dynamicFlowStep)),
          tap((dynamicFlowStep) => this.logDynamicFlowStep(dynamicFlowStep, flowId)),
          finalize(() => this.loadingNext.set(false))
        );
    }

    this.showValidationMessage.set(true);
    return EMPTY;
  }

  previous(flowId: string): Observable<DynamicFlowStep> {
    this.showValidationMessage.set(false);
    this.loadingPrevious.set(true);
    return this.dynamicFlowService
      .postStep(flowId, this.withContext(this.currentStep()), 'back')
      .pipe(
        tap((dynamicFlowStep) => this.currentStep.set(dynamicFlowStep)),
        tap((dynamicFlowStep) => this.logDynamicFlowStep(dynamicFlowStep, flowId)),
        finalize(() => this.loadingPrevious.set(false))
      );
  }

  updateAnswer(answer: DynamicFlowOption[]): void {
    this.showValidationMessage.set(false);
    const current = this.currentStep();
    this.currentStep.set({
      ...current,
      answer: {
        stepId: current.step?.id,
        selectedOptions: answer,
      },
    });
  }

  updateLocationAnswer(location: DynamicFlowLocationAnswer | null): void {
    this.showValidationMessage.set(false);
    const current = this.currentStep();
    this.currentStep.set({
      ...current,
      answer: location ? { stepId: current.step?.id, location } : null,
    });
  }

  updateFormAnswer(form: DynamicFlowRawFormValues | null): void {
    this.showValidationMessage.set(false);
    const current = this.currentStep();
    this.currentStep.set({
      ...current,
      answer: form ? { stepId: current.step?.id, formValues: this.toFormValues(form) } : null,
    });
  }

  private withContext(step: DynamicFlowStep): DynamicFlowStep {
    const context = this.context();
    return context ? { ...step, context } : step;
  }

  private toFormValues(form: DynamicFlowRawFormValues): DynamicFlowFormValue[] {
    return (this.formFields() ?? []).map((field) => ({
      fieldId: field.id,
      type: field.type,
      value: form[field.id],
    }));
  }

  private toFormControls(fields: DynamicFlowFormField[]): DynamicFormControl[] {
    const savedValues = untracked(() => this.currentStep()?.answer?.formValues ?? []);
    const valueByFieldId = new Map(savedValues.map((v) => [v.fieldId, v.value]));
    return fields.map((field) => ({
      name: field.id,
      type: field.type,
      label: field.label,
      value: valueByFieldId.get(field.id),
      validations: field.isRequired ? [{ name: 'required', validator: 'required' }] : [],
    }));
  }

  getPlacePredictions(input: string): Promise<google.maps.places.AutocompletePrediction[]> {
    return this.googleMapsService.getPlacePredictionsByGeocode(
      input,
      this.currentStep().step.location?.restrictions,
    );
  }

  getPlaceDetails(placeId: string): Promise<PlaceDetails | null> {
    return this.googleMapsService.geocodePlaceDetails(placeId);
  }

  private isLocationGranted(status: PermissionStatus): boolean {
    return status.location === 'granted' || status.coarseLocation === 'granted';
  }

  private async ensureLocationPermission(): Promise<boolean> {
    const status = await Geolocation.checkPermissions();
    if (this.isLocationGranted(status)) {
      return true;
    }

    const requested = await Geolocation.requestPermissions({ permissions: ['location', 'coarseLocation'] });
    return this.isLocationGranted(requested);
  }

  async getCurrentLocationDetails(): Promise<PlaceDetails | null> {
    const granted = await this.ensureLocationPermission();
    if (!granted) {
      this.toastSandbox.showToast({ key: 'dynamic-flow.location-step.use-current-location-permission-denied' });
      return null;
    }

    let position: Awaited<ReturnType<typeof Geolocation.getCurrentPosition>>;
    try {
      position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10_000 });
    } catch {
      this.toastSandbox.showErrorToast({ key: 'dynamic-flow.location-step.use-current-location-unavailable' });
      return null;
    }

    const details = await this.googleMapsService.geocodePlaceDetailsByCoordinates({
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    });
    if (!details) {
      this.toastSandbox.showErrorToast({ key: 'dynamic-flow.location-step.use-current-location-unavailable' });
      return null;
    }

    return details;
  }

  cleanup(): void {
    this.currentStep.set(null);
    this.context.set(null);
    this.showValidationMessage.set(false);
    this.loadingFlow.set(false);
    this.loadingNext.set(false);
    this.loadingPrevious.set(false);
  }

  logDynamicFlowComplete(flowId: string): void {
    this.deeplinkService.logBranchEvent(`${AnalyticsEvents.ProcessCompleted}_${flowId}`);
    this.analyticsService.logEvent(AnalyticsEvents.ProcessCompleted, {
      name: flowId,
    });
  }

  private logDynamicFlowStep(step: DynamicFlowStep, flowId: string): void {
    this.analyticsService.logEvent(AnalyticsEvents.Process, {
      name: flowId,
      step: step.step?.id,
    });
  }

  private isCurrentStepValid(): boolean {
    return this.currentStep().step.isOptional || Boolean(this.currentStep().answer);
  }
}
